import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Form, Modal, Spinner, Table } from "react-bootstrap";
import useReactRouter from "use-react-router";
import moment from "moment";
import Switch from "react-switch";
import { useToasts } from "react-toast-notifications";
import { useLazyQuery, useMutation } from "@apollo/client";
import { COLOR_APP, FACEBOOK_API_VERSION } from "../../constants";
import { GET_POSTS } from "../../graphql/post/query";
import { ENSURE_POST_FOR_FEED, START_HOOK_POST, STOP_HOOK_POST } from "../../graphql/post/mutation";
import EmptyComponent from "../../components/EmptyComponent";
import PaginationHelper from "../../helpers/PaginationHelper";
import { onSelectedPageChange, readSelectedPage, saveSelectedPage } from "../../helpers/selectedPage";
import { useShopPages } from "../../helpers/shopPages";

/**
 * ຮັບອໍເດີທຸກໂພສ — every post on the shop's page, each with its own take-orders switch.
 *
 * Shown instead of PostFeed when the shop's isCfAllPosts setting is on (see PostFeedGate).
 * The posts come from Facebook, as on the create-post screen; whether each one takes orders
 * comes from the matching 4B post's status. A post with no 4B post yet shows OFF — creating
 * one, switched off, is ensurePostForFeed's job and only happens when the shop acts on it.
 *
 * Switching ON is exactly the old screen's "open post": ensure the 4B post, then
 * startHookPost. Switching OFF is stopHookPost. Opening a post goes to the unchanged
 * PostDetail with the 4B post, as the old screen does.
 */
const FEED_FIELDS = "id,message,created_time,full_picture";
const FEED_LIMIT = 25;
//* "pageId_objectId" -> "objectId", the form PostFeedAdd stores and the consumer matches on.
const objectIdOf = (id) => String(id || "").split("_").pop();

//* The first line of the post, short enough for a table cell.
const titleOf = (message) => {
  const first = String(message || "").split("\n").map((l) => l.trim()).find(Boolean) || "";
  return first.length > 90 ? `${first.slice(0, 90)}…` : first;
};

export default function PostFeedAllPosts() {
  const { history } = useReactRouter();
  const { addToast } = useToasts();
  const shopId = localStorage.getItem("SHOP");

  //* Live: a page connected while this screen is open shows at once, no refresh.
  const pages = useShopPages();
  //* The same page as the chat inbox (helpers/selectedPage): picked here, chat opens on it too,
  //* and the other way round. Falls back to the first page when that one is not connected here.
  const [pageId, setPageIdState] = useState(() => {
    const saved = readSelectedPage();
    return pages.some((p) => p.id === saved) ? saved : (pages[0]?.id || "");
  });
  const setPageId = (id) => {
    setPageIdState(id);
    saveSelectedPage(id);
  };
  useEffect(() => onSelectedPageChange((id) => {
    if (pages.some((p) => p.id === id)) setPageIdState(id);
  }), [pages]);
  //* Pages arrived (Facebook just connected) or the chosen one went: pick the saved one, or the first.
  useEffect(() => {
    if (pages.some((p) => p.id === pageId)) return;
    const saved = readSelectedPage();
    setPageIdState(pages.some((p) => p.id === saved) ? saved : (pages[0]?.id || ""));
  }, [pages, pageId]);
  const page = pages.find((p) => p.id === pageId) || null;

  /**
   * PAGES of the feed, as Facebook hands them out: 25 posts each, newest first.
   *
   * Facebook gives a cursor to the next page and no total, so the pager shows the pages reached
   * so far plus ຖັດໄປ. Pages already loaded are kept, so going back is instant and fetches
   * nothing; only stepping past the last one asks Facebook.
   */
  const [feedPages, setFeedPages] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const feed = feedPages[pageIndex] || [];
  //* The cursor after the LAST loaded page; null once Facebook says there is no more.
  const [nextUrl, setNextUrl] = useState(null);
  const [loadingFeed, setLoadingFeed] = useState(false);
  const [feedError, setFeedError] = useState(null);
  //* Feed id of the row being switched or opened, so only that row shows a spinner.
  const [busyId, setBusyId] = useState(null);

  const [getPosts, { data: postsData, refetch: refetchPosts }] = useLazyQuery(GET_POSTS, {
    fetchPolicy: "network-only",
  });
  const [ensurePost] = useMutation(ENSURE_POST_FOR_FEED);
  const [startHookPost] = useMutation(START_HOOK_POST);
  const [stopHookPost] = useMutation(STOP_HOOK_POST);

  //* Facebook feed id -> 4B post, by either field: an old-screen post can group several.
  //* Keyed by object id: the feed gives "page_object", 4B posts store "object".
  const postByFeed = useMemo(() => {
    const map = new Map();
    for (const p of postsData?.posts?.data || []) {
      const ids = [p.fbFeedId, ...(p.fbFeedIds || [])].filter(Boolean).map(objectIdOf);
      for (const id of ids) if (!map.has(id)) map.set(id, p);
    }
    return map;
  }, [postsData]);

  const loadPosts = useCallback(() => {
    if (!shopId || !pageId) return;
    getPosts({
      //* Newest first, so a feed id grouped under two posts maps to the one ensurePostForFeed picks.
      variables: { where: { shop: shopId, page: pageId, isDeleted: false }, limit: 1000, skip: 0, orderBy: "createdAt_DESC" },
    });
  }, [getPosts, shopId, pageId]);

  //* The page on screen right now, for Facebook callbacks that may land after a switch.
  const currentPage = useRef(pageId);
  currentPage.current = pageId;

  /**
   * Straight to the Graph API with the page's own token — not through the JS SDK.
   *
   * This waited for useInitFbSDK() to report the SDK ready, and in production it never does:
   * setIsInitialized(true) is commented out in helpers/fb-hooks.js, so the flag stays false and
   * the list never loaded — an empty screen with no error. Turning that flag back on would wake
   * the login-status check in the global Facebook state on every screen, so this needs no SDK at
   * all instead: a page token is all the feed requires, and `paging.next` is already a full URL.
   */
  const loadFeed = useCallback(async (url, index) => {
    setLoadingFeed(true);
    setFeedError(null);
    const forPage = currentPage.current;
    let response;
    try {
      response = await (await fetch(url)).json();
    } catch (e) {
      response = { error: { message: e?.message || "network error" } };
    }
    //* A slow answer for the page the shop just left must not fill the new page's list.
    if (currentPage.current !== forPage) return;
    setLoadingFeed(false);
    if (!response || response.error) {
      setFeedError(response?.error?.message || "error");
      return;
    }
    const rows = response.data || [];
    //* Facebook can hand out a cursor to an empty page at the very end: that is "no more".
    if (index > 0 && !rows.length) {
      setNextUrl(null);
      return;
    }
    setFeedPages((prev) => { const next = prev.slice(0, index); next[index] = rows; return next; });
    setPageIndex(index);
    setNextUrl(response?.paging?.next || null);
  }, []);

  const { Pagination_state } = PaginationHelper();
  const goToPage = (i) => {
    if (i < 0 || loadingFeed || bulk || i === pageIndex) return;
    if (i < feedPages.length) setPageIndex(i);
    else if (i === feedPages.length && nextUrl) loadFeed(nextUrl, i);
    else return;
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const hasNext = pageIndex < feedPages.length - 1 || !!nextUrl;

  useEffect(() => {
    if (!page?.access_token) return;
    setFeedPages([]);
    setPageIndex(0);
    setNextUrl(null);
    loadFeed(`https://graph.facebook.com/${FACEBOOK_API_VERSION}/${page.id}/feed?access_token=${encodeURIComponent(page.access_token)}&limit=${FEED_LIMIT}&fields=${FEED_FIELDS}`, 0);
    loadPosts();
  }, [page, loadFeed, loadPosts]);

  //* The 4B post for this Facebook post, created switched off if there is none yet.
  const ensure = async (item) => {
    const { data } = await ensurePost({
      variables: {
        data: {
          shop: shopId,
          page: page.id,
          pageName: page.name || "",
          fbFeedId: objectIdOf(item.id),
          title: titleOf(item.message),
          detail: item.message || "",
        },
      },
    });
    return data?.ensurePostForFeed;
  };

  //* One post on or off. No toast and no refetch, so the switch-all below can run it many times.
  const switchOne = async (item, next) => {
    const post = await ensure(item);
    if (!post?.id) throw new Error("no post");
    if (next) {
      await startHookPost({ variables: { where: { id: post.id, pageId: page.id, access_token: page.access_token } } });
    } else {
      await stopHookPost({ variables: { where: { id: post.id } } });
    }
  };

  const onToggle = async (item, next) => {
    if (!page?.access_token) {
      addToast("ກະລຸນາເຊື່ອມ Facebook ກ່ອນ!", { appearance: "warning", autoDismiss: true });
      return;
    }
    setBusyId(item.id);
    try {
      await switchOne(item, next);
      await refetchPosts?.();
      addToast(next ? "ເປີດຮັບອໍເດີໂພສນີ້ແລ້ວ" : "ປິດຮັບອໍເດີໂພສນີ້ແລ້ວ", { appearance: "success", autoDismiss: true });
    } catch (error) {
      console.log("toggle post error: ", error);
      addToast("ບັນທຶກບໍ່ສຳເລັດ. ກະລຸນາເຊື່ອມເຟສໃໝ່ແລ້ວລອງໃໝ່ອີກຄັ້ງ", { appearance: "warning", autoDismiss: true });
    } finally {
      setBusyId(null);
    }
  };

  /**
   * EVERY post on the Facebook page on (or off) at once — all of it, not just the 25 on screen.
   *
   * The whole feed is fetched first (100 a request, the most Facebook gives), so the shop is
   * asked to confirm the real number. It is then laid out as the pager's pages, so afterwards
   * every page of the list is loaded and the master switch speaks for the whole Facebook page.
   * Only posts not already in that state are touched, four at a time; one failure does not stop
   * the rest, and the count of failures is reported.
   */
  const [bulk, setBulk] = useState(null); // { label, done, total } while running
  const isOn = (item) => postByFeed.get(objectIdOf(item.id))?.status === "ONAIR";
  const loadedAll = feedPages.length > 0 && !nextUrl;
  //* Shown from the posts loaded so far (the rest are unknown until fetched); flipping it always
  //* acts on the whole Facebook page.
  const allOn = feedPages.some((rows) => rows.length) && feedPages.every((rows) => rows.every(isOn));

  //* A post count that is still sane to switch in one go; beyond it the shop is told, not stuck.
  const ALL_POSTS_CAP = 3000;
  const fetchWholeFeed = async () => {
    const forPage = currentPage.current;
    let url = `https://graph.facebook.com/${FACEBOOK_API_VERSION}/${page.id}/feed?access_token=${encodeURIComponent(page.access_token)}&limit=100&fields=${FEED_FIELDS}`;
    const all = [];
    while (url && all.length < ALL_POSTS_CAP) {
      const r = await (await fetch(url)).json();
      if (!r || r.error) throw new Error(r?.error?.message || "feed error");
      if (currentPage.current !== forPage) throw new Error("page changed");
      all.push(...(r.data || []));
      setBulk({ label: "ກຳລັງໂຫຼດໂພສ", done: all.length, total: null });
      url = (r.data || []).length ? r?.paging?.next : null;
    }
    const pagesOf = [];
    for (let k = 0; k < all.length; k += FEED_LIMIT) pagesOf.push(all.slice(k, k + FEED_LIMIT));
    setFeedPages(pagesOf.length ? pagesOf : [[]]);
    setPageIndex(0);
    setNextUrl(null);
    return { all, truncated: !!url };
  };

  //* The confirm for switch-all, as the app's own popup rather than the browser's alert box.
  const [confirmAll, setConfirmAll] = useState(null); // { next, count, total, truncated, resolve }
  const askConfirm = (info) => new Promise((resolve) => setConfirmAll({ ...info, resolve }));
  const answerConfirm = (yes) => { const c = confirmAll; setConfirmAll(null); if (c) c.resolve(yes); };

  const onToggleAll = async (next) => {
    if (!page?.access_token) {
      addToast("ກະລຸນາເຊື່ອມ Facebook ກ່ອນ!", { appearance: "warning", autoDismiss: true });
      return;
    }
    let all;
    let truncated = false;
    try {
      setBulk({ label: "ກຳລັງໂຫຼດໂພສ", done: 0, total: null });
      ({ all, truncated } = await fetchWholeFeed());
    } catch (e) {
      setBulk(null);
      console.log("switch all: could not load the feed", e?.message || e);
      addToast("ໂຫຼດໂພສທັງໝົດບໍ່ສຳເລັດ ກະລຸນາລອງໃໝ່", { appearance: "warning", autoDismiss: true });
      return;
    }
    const todo = all.filter((item) => isOn(item) !== next);
    if (!todo.length) {
      setBulk(null);
      addToast(next ? "ທຸກໂພສເປີດຢູ່ແລ້ວ" : "ທຸກໂພສປິດຢູ່ແລ້ວ", { appearance: "info", autoDismiss: true });
      return;
    }
    const go = await askConfirm({ next, count: todo.length, total: all.length, truncated });
    if (!go) { setBulk(null); return; }

    const label = next ? "ກຳລັງເປີດ" : "ກຳລັງປິດ";
    setBulk({ label, done: 0, total: todo.length });
    let done = 0;
    let failed = 0;
    const queue = [...todo];
    const worker = async () => {
      while (queue.length) {
        const item = queue.shift();
        try { await switchOne(item, next); } catch (e) { failed++; console.log("switch all: post failed", item.id, e?.message || e); }
        done++;
        setBulk({ label, done, total: todo.length });
      }
    };
    await Promise.all([worker(), worker(), worker(), worker()]);
    await refetchPosts?.();
    setBulk(null);
    const ok = todo.length - failed;
    addToast(
      failed
        ? `${next ? "ເປີດ" : "ປິດ"}ແລ້ວ ${ok} ໂພສ, ບໍ່ສຳເລັດ ${failed} ໂພສ — ກະລຸນາລອງໃໝ່`
        : `${next ? "ເປີດ" : "ປິດ"}ຮັບອໍເດີແລ້ວ ${ok} ໂພສ`,
      { appearance: failed ? "warning" : "success", autoDismiss: true }
    );
  };

  const onOpen = async (item) => {
    setBusyId(item.id);
    try {
      const post = postByFeed.get(objectIdOf(item.id)) || (await ensure(item));
      if (!post?.id) throw new Error("no post");
      history.push(`/post-detail`, { live: post });
    } catch (error) {
      console.log("open post error: ", error);
      addToast("ເປີດໂພສບໍ່ສຳເລັດ ກະລຸນາລອງໃໝ່", { appearance: "warning", autoDismiss: true });
      setBusyId(null);
    }
  };

  if (!pages.length) {
    return (
      <div className="p-4">
        <Card body>ກະລຸນາເຊື່ອມ Facebook ກ່ອນ</Card>
      </div>
    );
  }

  return (
    <div className="p-3">
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3" style={{ gap: 10 }}>
        <div>
          <h4 className="mb-1">ໂພສທັງໝົດໃນເພຈ</h4>
          <div className="text-muted" style={{ fontSize: 14 }}>
            ເປີດຮັບອໍເດີໂພສໃດກໍໄດ້ — ລູກຄ້າ CF ສິນຄ້າໃດກໍໄດ້ທີ່ມີໃນຮ້ານ
          </div>
        </div>
        {pages.length > 1 && (
          <Form.Control as="select" value={pageId} disabled={!!bulk} onChange={(e) => setPageId(e.target.value)} style={{ maxWidth: 280 }}>
            {pages.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </Form.Control>
        )}
      </div>

      {feedError && (
        <Card body className="mb-3 text-danger">
          ໂຫຼດໂພສຈາກ Facebook ບໍ່ສຳເລັດ — ກະລຸນາເຊື່ອມເຟສໃໝ່ ({String(feedError)})
        </Card>
      )}

      <Card>
        {/* The table scrolls inside its own box so the page never scrolls sideways. */}
        <div style={{ overflowX: "auto" }}>
          <Table hover className="mb-0" style={{ minWidth: 720 }}>
            <thead>
              <tr>
                <th style={{ width: 90 }}>ຮູບ</th>
                <th>ຊື່ໂພສ</th>
                <th style={{ whiteSpace: "nowrap" }}>ເວລາສ້າງ</th>
                <th style={{ whiteSpace: "nowrap" }}>
                  <div>ຮັບອໍເດີ</div>
                  {feed.length > 0 && (
                    <div className="d-flex align-items-center" style={{ gap: 6, fontWeight: "normal", fontSize: 13, marginTop: 4 }}>
                      {bulk ? (
                        <>
                          <Spinner animation="border" size="sm" />
                          <span>{bulk.label} {bulk.total ? `${bulk.done}/${bulk.total}` : bulk.done}</span>
                        </>
                      ) : (
                        <>
                          <Switch
                            checked={allOn}
                            onChange={(next) => onToggleAll(next)}
                            disabled={!!busyId}
                            onColor="#bfbdc7"
                            onHandleColor="#422f90"
                            handleDiameter={16}
                            uncheckedIcon={false}
                            checkedIcon={false}
                            height={22}
                            width={44}
                          />
                          <span>ທຸກໂພສໃນເພຈ{loadedAll ? ` (${feedPages.reduce((n, r) => n + r.length, 0)})` : ""}</span>
                        </>
                      )}
                    </div>
                  )}
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {!loadingFeed && feed.length === 0 && !feedError && (
                <tr>
                  <td colSpan={5}><EmptyComponent /></td>
                </tr>
              )}
              {feed.map((item) => {
                const post = postByFeed.get(objectIdOf(item.id));
                const on = post?.status === "ONAIR";
                const busy = busyId === item.id;
                return (
                  <tr key={item.id}>
                    <td style={{ verticalAlign: "middle" }}>
                      {item.full_picture ? (
                        <img src={item.full_picture} alt="" style={{ width: 70, height: 70, objectFit: "cover", borderRadius: 6 }} />
                      ) : (
                        <div style={{ width: 70, height: 70, borderRadius: 6, background: "#f0f0f0" }} />
                      )}
                    </td>
                    <td style={{ verticalAlign: "middle", cursor: "pointer" }} onClick={() => !busy && onOpen(item)}>
                      {titleOf(item.message) || <span className="text-muted">(ບໍ່ມີຂໍ້ຄວາມ)</span>}
                    </td>
                    <td style={{ verticalAlign: "middle", whiteSpace: "nowrap" }}>
                      {moment(item.created_time).format("DD/MM/YYYY HH:mm")}
                    </td>
                    <td style={{ verticalAlign: "middle" }}>
                      {busy ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <Switch
                          checked={on}
                          onChange={(next) => onToggle(item, next)}
                          disabled={!!busyId || !!bulk}
                          onColor="#bfbdc7"
                          onHandleColor="#422f90"
                          handleDiameter={20}
                          uncheckedIcon={false}
                          checkedIcon={false}
                          height={28}
                          width={55}
                        />
                      )}
                    </td>
                    <td style={{ verticalAlign: "middle", textAlign: "right" }}>
                      <Button size="sm" disabled={!!busyId || !!bulk} onClick={() => onOpen(item)}
                        style={{ background: COLOR_APP, border: "none", color: "#fff", whiteSpace: "nowrap" }}>
                        ລາຍລະອຽດ
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
        {(feedPages.length > 1 || hasNext) && (
          <Card.Footer className="d-flex justify-content-center align-items-center" style={{ gap: 10 }}>
            {/* The app's pager (helpers/PaginationHelper). One page more than loaded while
                Facebook has more: picking it loads it. */}
            <div style={{ overflowX: "auto" }}>
              {Pagination_state(feedPages.length + (nextUrl ? 1 : 0), pageIndex, goToPage)}
            </div>
            {loadingFeed && <Spinner animation="border" size="sm" />}
          </Card.Footer>
        )}
      </Card>

      <Modal show={!!confirmAll} onHide={() => answerConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 18 }}>
            {confirmAll?.next ? "ເປີດຮັບອໍເດີທຸກໂພສ" : "ປິດຮັບອໍເດີທຸກໂພສ"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: 15 }}>
          {confirmAll?.next ? (
            <>
              ເປີດຮັບອໍເດີ <b>{confirmAll?.count}</b> ໂພສ (ຈາກທັງໝົດ {confirmAll?.total} ໂພສໃນເພຈ {page?.name}).
              <div className="text-muted mt-2">ລູກຄ້າຈະ CF ສິນຄ້າໄດ້ທຸກໂພສທີ່ເປີດ.</div>
            </>
          ) : (
            <>
              ປິດຮັບອໍເດີ <b>{confirmAll?.count}</b> ໂພສໃນເພຈ {page?.name}.
              <div className="text-muted mt-2">ລູກຄ້າຈະ CF ໂພສເຫຼົ່ານີ້ບໍ່ໄດ້.</div>
            </>
          )}
          {confirmAll?.truncated && (
            <div className="text-muted mt-2">ລວມສະເພາະ {ALL_POSTS_CAP} ໂພສລ່າສຸດ — ໂພສເກົ່າກວ່ານັ້ນບໍ່ລວມ.</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => answerConfirm(false)}>ຍົກເລີກ</Button>
          <Button onClick={() => answerConfirm(true)} style={{ background: COLOR_APP, border: "none" }}>
            {confirmAll?.next ? "ເປີດທັງໝົດ" : "ປິດທັງໝົດ"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
